// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { CircleNotch, UserPlus } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import {
  BREADCRUMB_HEADER_CHIP_CLASS,
  BREADCRUMB_TITLE_TEXT_CLASS,
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import { useAppSelector } from "../../utils/hooks";
import {
  useAskPunttProject,
  useProjectReviews,
} from "../../utils/queries/askPunttProjects";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { AskV2ProjectTabs } from "../conversation/AskV2ProjectTabs";
import { ASK_V2_PROJECTS_PATH } from "../paths";
import { AskV2RailToggle } from "../rail/AskV2RailToggle";
import { AskV2RightRail } from "../rail/AskV2RightRail";
import { collectRailContent, hasRailContent } from "../rail/collectRailContent";
import { type AskV2RailSectionId } from "../rail/types";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { AskV2ThreadLoading } from "../transport/ThreadLoading";
import { useAskV2Transport } from "../transport/useAskV2Transport";

type AskV2ProjectPageProps = {
  projectId: string;
};

const DEFAULT_SECTIONS: Record<AskV2RailSectionId, boolean> = {
  progress: true,
  output: true,
  context: true,
};

export function AskV2ProjectPage({ projectId }: AskV2ProjectPageProps) {
  const { t } = useTranslation("ui");
  const [tab, setTab] = useState<"feed" | "chats">("feed");
  const [railOpen, setRailOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState(DEFAULT_SECTIONS);
  const projectQuery = useAskPunttProject(projectId);
  const reviewsQuery = useProjectReviews(projectId);
  const project = projectQuery.data?.project;
  const members = project?.members ?? [];
  const sharedChatId = project?.sharedChatId ?? null;
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const viewer = members.find((member) => member._id === currentUserId);
  const transport = useAskV2Transport({
    chatId: tab === "feed" ? sharedChatId : null,
    enabled: tab === "feed" && !!sharedChatId,
    presentation: "feed",
  });
  const owner = members.find((member) => member._id === project?.ownerId);
  const ownerName = owner?.name ?? owner?.email ?? "Owner";
  const railContent = useMemo(
    () =>
      collectRailContent({
        project,
        chat: transport.historyChat,
        messages: transport.historyMessages,
        reviews: reviewsQuery.data,
      }),
    [
      project,
      reviewsQuery.data,
      transport.historyChat,
      transport.historyMessages,
    ],
  );
  const showRail = railOpen && hasRailContent(railContent);

  if (projectQuery.isLoading) {
    return (
      <div
        className="ask-puntt-surface flex h-full items-center justify-center"
        data-testid="ask-v2-project-loading"
      >
        <CircleNotch
          size={28}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (projectQuery.isError || !project) {
    return (
      <div
        className="ask-puntt-surface flex h-full flex-col items-center justify-center gap-4"
        data-testid="ask-v2-project-error"
      >
        <Text size="4" weight="medium" className="text-puntt-neutral-gray-11">
          {t("campaign.couldNotLoadProject")}
        </Text>
        <a
          href={ASK_V2_PROJECTS_PATH}
          className="text-sm text-puntt-accent-9 hover:underline"
          data-testid="ask-v2-project-error-back"
        >
          {t("campaign.backToAllProjects")}
        </a>
      </div>
    );
  }

  return (
    <AskV2ConversationShell
      testId="ask-v2-project-page"
      onAddQuotedContext={transport.addQuotedContext}
      header={
        <PageHeader className="w-full min-w-0 max-w-full gap-3 overflow-hidden border-puntt-neutral-gray-5">
          <BreadcrumbHeaderTrail className="min-w-0 flex-1 gap-6 overflow-hidden">
            <BreadcrumbHeaderRoot
              to={ASK_V2_PROJECTS_PATH}
              label={t("allProjects")}
              testId="ask-v2-project-breadcrumb-all"
            />
            <span
              className={`${BREADCRUMB_TITLE_TEXT_CLASS} truncate`}
              data-testid="ask-v2-project-title"
            >
              {project.name}
            </span>
            <span
              className={`hidden shrink-0 rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[11px] font-medium text-puntt-neutral-gray-11 sm:inline ${BREADCRUMB_HEADER_CHIP_CLASS}`}
              data-testid="ask-v2-project-owner"
            >
              {t("ownedBy")} {ownerName}
            </span>
            <span className="sr-only" data-testid="ask-v2-project-id">
              {projectId}
            </span>
          </BreadcrumbHeaderTrail>
          {members.length > 0 ? (
            <div
              className="flex shrink-0 items-center -space-x-2"
              data-testid="ask-v2-project-members"
            >
              {members.slice(0, 5).map((member) => (
                <AvatarWithInitials
                  key={member._id}
                  size={7}
                  name={member.name ?? member.email ?? "Member"}
                  avatar={member.avatar ?? undefined}
                  colorKey={member._id}
                  className="ring-2 ring-puntt-neutral-gray-1"
                />
              ))}
              {members.length > 5 ? (
                <span
                  className="flex size-7 items-center justify-center rounded-full bg-puntt-neutral-gray-3 text-[10px] font-medium text-puntt-neutral-gray-11 ring-2 ring-puntt-neutral-gray-1"
                  data-testid="ask-v2-project-members-overflow"
                >
                  +{members.length - 5}
                </span>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            disabled
            aria-disabled="true"
            aria-label={t("inviteToProject")}
            data-testid="ask-v2-project-invite"
            className="flex shrink-0 cursor-not-allowed items-center gap-1.5 rounded-md border border-puntt-neutral-gray-7 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 opacity-60"
          >
            <UserPlus size={15} />
            {t("invite")}
          </button>
          <AskV2RailToggle
            open={railOpen}
            onToggle={() => setRailOpen((current) => !current)}
          />
        </PageHeader>
      }
      tabs={
        <AskV2ProjectTabs
          data-testid="ask-v2-project-tabs"
          active={tab}
          feedLabel={t("sharedProjectFeed")}
          chatsLabel={t("privateChats")}
          viewsLabel={t("projectViews")}
          onChange={setTab}
        />
      }
      composer={<AskV2SummonBar transport={transport} requireBoundChatId />}
      rail={
        showRail ? (
          <AskV2RightRail
            content={railContent}
            sectionOpen={sectionOpen}
            onSectionOpenChange={(id, open) =>
              setSectionOpen((current) => ({ ...current, [id]: open }))
            }
          />
        ) : null
      }
    >
      {tab === "chats" ? (
        <p
          className="py-8 text-center text-[11px] font-medium uppercase tracking-[0.08em] text-puntt-neutral-gray-9"
          data-testid="ask-v2-project-chats-empty"
        >
          {t("campaignNewChatDraft.emptyLabel")}
        </p>
      ) : !sharedChatId ? (
        <p
          className="py-8 text-center text-sm text-puntt-neutral-gray-10"
          data-testid="ask-v2-project-feed-empty"
        >
          {t("campaignNewChatDraft.emptyLabel")}
        </p>
      ) : transport.historyLoading && transport.messages.length === 0 ? (
        <AskV2ThreadLoading />
      ) : transport.historyError && transport.messages.length === 0 ? (
        <Text
          as="p"
          size="2"
          color="gray"
          data-testid="ask-v2-thread-error"
          className="py-8 text-center"
        >
          {t("askV2.pages.chat.loadError")}
        </Text>
      ) : (
        <AskV2Thread
          transport={transport}
          presentation="feed"
          feedActorName={viewer?.name ?? viewer?.email ?? undefined}
          feedActorAvatar={viewer?.avatar ?? undefined}
          feedMembers={members}
          currentUserId={currentUserId}
        />
      )}
    </AskV2ConversationShell>
  );
}
